import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from "react-csv";
import { useTranslation } from 'react-i18next';
import func from '../../services/functions';
import { Menu } from 'primereact/menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import TooltipOverlay from './TooltipOverlay';
import { toast } from "react-toastify";

const ExportCsvMultiple = ({ data, columns, selectedColumns, excludedColumns = [], onClick, callback, asDropdown = false }) => {
  const { t, i18n } = useTranslation("translations");
  const menuRef = useRef(null);
  const csvSimpleRef = useRef(null);
  const csvAllRef = useRef(null);
  const pendingCsvRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [exportTrigger, setExportTrigger] = useState(0);

  const allCols = useMemo(() => {
    if (!selectedColumns) return [];
    const newCols = columns.filter(col => selectedColumns?.every((sCol => sCol.field !== col?.field))).map(foundCol => ({ ...foundCol, show: !!foundCol.persisted, newCol: true }));
    let currCols = [];
    selectedColumns.forEach(sCol => {
      const currCol = columns.find(col => col.field === sCol.field);
      if (currCol) currCols.push({ ...currCol, show: sCol.show });
    });
    return [...newCols, ...currCols];
  }, [selectedColumns, columns, i18n.language]);

  // Get only visible/selected columns
  const filteredColumns = useMemo(() => {
    let _filteredColumns = [];
    selectedColumns.forEach((persistedCol) => {
      const column = columns?.find((col) => col.field === persistedCol?.field);
      if (column && (persistedCol.show || column.persisted)) _filteredColumns.push(column);
    });
    return _filteredColumns;
  }, [selectedColumns, columns]);

  const getExportColumns = (simpleExport) => {
    const cols = simpleExport ? filteredColumns : allCols;
    return cols?.map((col) => ({
      ...col,
      field: col.field,
      header: col.header ?? func.camelToFlat(col.field),
    })) ?? [];
  };

  const sourceData = exportData ?? data;

  const formatDataForExport = (exportColumns) => {
    if (sourceData?.length > 0) {
      return sourceData.map((invoice) => {
        const formattedObject = {};
        exportColumns.forEach((col) => {
          if (!excludedColumns?.find(excCol => excCol === col.field)) {
            const head = col?.exportHeader ?? col.header;
            const bodyValue = col?.body ? col.body(invoice) : null;

            if (col.format) {
              formattedObject[head] = col.format(invoice);
            } else if (col?.formattedExportValue) {
              formattedObject[head] = bodyValue;
            } else {
              formattedObject[head] = invoice[col.field];
            }
          }
        });
        if (callback !== undefined) {
          callback(invoice, formattedObject);
        }
        return formattedObject;
      });
    } else {
      const dummy = {};
      exportColumns.forEach((col) => {
        if (!excludedColumns?.find(excCol => excCol === col.field)) {
          const head = col?.exportHeader ?? col.header;
          dummy[head] = null;
        }
      });
      return [dummy];
    }
  };

  const simpleExportData = useMemo(() => formatDataForExport(getExportColumns(true)), [sourceData, filteredColumns, excludedColumns, callback]);
  const allExportData = useMemo(() => formatDataForExport(getExportColumns(false)), [sourceData, allCols, excludedColumns, callback]);

  useEffect(() => {
    if (!exportTrigger || !pendingCsvRef.current) return;

    const csvRef = pendingCsvRef.current;
    pendingCsvRef.current = null;
    const timeout = setTimeout(() => {
      csvRef.current?.link?.click();
      setExportData(null);
    }, 0);

    return () => clearTimeout(timeout);
  }, [exportTrigger, simpleExportData, allExportData]);

  const triggerCsvDownload = (csvRef) => {
    pendingCsvRef.current = csvRef;
    setExportTrigger((trigger) => trigger + 1);
  };

  const handleExport = (csvRef) => {
    if (loading) return;

    if (onClick === undefined) {
      triggerCsvDownload(csvRef);
      return;
    }

    setLoading(true);
    const loadingToastId = toast.loading("Exporting...", {
      containerId: "all_toast",
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: false,
    });

    Promise.resolve()
      .then(() => onClick())
      .then((res) => {
        const exportRows = Array.isArray(res) ? res : null;
        if (exportRows) setExportData(exportRows);
        setLoading(false);
        toast.dismiss(loadingToastId);
        triggerCsvDownload(csvRef);
      })
      .catch(() => {
        setLoading(false);
        toast.dismiss(loadingToastId);
        triggerCsvDownload(csvRef);
      });
  };

  // Dropdown menu items
  const menuItems = useMemo(() => [
    {
      label: 'Export',
      template: (item, options) => (
        <a
          className={options.className}
          style={{ cursor: 'pointer' }}
          onClick={(e) => { options.onClick(e); handleExport(csvSimpleRef); }}
          title={t("books.onlyGridColumns")}
        >
          <span className="p-menuitem-text">Export</span>
        </a>
      )
    },
    {
      label: 'Export All',
      template: (item, options) => (
        <a
          className={options.className}
          style={{ cursor: 'pointer' }}
          onClick={(e) => { options.onClick(e); handleExport(csvAllRef); }}
          title={t("books.allColumns")}
        >
          <span className="p-menuitem-text">Export All</span>
        </a>
      )
    }
  ], [t, loading, onClick]);

  if (asDropdown) {
    return (
      <>
        <Menu model={menuItems} popup ref={menuRef} appendTo={document.body} />
        <TooltipOverlay title="Export CSV" placement="bottom">
          <div
            onClick={(e) => menuRef.current?.toggle(e)}
            className={`prime-table-toolbar__icon-btn ${loading ? 'prime-table-toolbar__icon-btn--loading' : ''}`}
          >
            <FontAwesomeIcon icon={faFileExcel} />
          </div>
        </TooltipOverlay>
        {/* Hidden CSV Links */}
        <div style={{ display: 'none' }}>
          <CSVLink
            ref={csvSimpleRef}
            data={simpleExportData}
            filename={`export_${new Date().getTime()}.csv`}
          />
          <CSVLink
            ref={csvAllRef}
            data={allExportData}
            filename={`export_all_${new Date().getTime()}.csv`}
          />
        </div>
      </>
    );
  }

  // Non-dropdown version with two buttons
  return (
    <div className='d-flex align-items-center'>
      <small>
        <TooltipOverlay title={t("books.onlyGridColumns")} placement="bottom">
          <div
            onClick={() => handleExport(csvSimpleRef)}
            className={`btn btn-light bg-white shadow-sm btn-custom btn-sm text-primary ${loading ? "disabled" : ""}`}
          >
            <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
            <span className="text-dark">Export</span>
          </div>
        </TooltipOverlay>
        <CSVLink
          ref={csvSimpleRef}
          data={simpleExportData}
          filename={`export_${new Date().getTime()}.csv`}
          className="hidden"
        />
      </small>
      <small className="ml-2">
        <TooltipOverlay title={t("books.allColumns")} placement="bottom">
          <div
            onClick={() => handleExport(csvAllRef)}
            className={`btn btn-light bg-white shadow-sm btn-custom btn-sm text-primary ${loading ? "disabled" : ""}`}
          >
            <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
            <span className="text-dark">Export All</span>
          </div>
        </TooltipOverlay>
        <CSVLink
          ref={csvAllRef}
          data={allExportData}
          filename={`export_all_${new Date().getTime()}.csv`}
          className="hidden"
        />
      </small>
    </div>
  );
};

export default ExportCsvMultiple;
